import React from 'react';
import { deleteAgentprofile, } from '@Api';
import { IAbilities, IAuth, IOrganizationStore, IPauseStore, IQueueStore, ITeamStore, } from '@Store';
import { CrudEnum, IPreviewAgentprofile, PermissionEnum, TAgentprofileSortableListCol, } from 'atlas-shared';
import { tableColumnDatetimeSorter, tableColumnStringSorter, timeSince, } from '@Utils';
import { TFunction, } from 'i18next';
import { NavigateFunction, } from 'react-router';
import { ColumnsType, } from 'antd/lib/table/interface';
import { listAuditButton, listCloneButton, listDeleteButton, listEditButton, GetColumnSearchProps, GetColumnSearchLikeProps, GetColumnSearchEqualProps, } from '../components';
import { IWithListProps, } from '@Hocs/with.list';

export const AgentprofileListColumns = (t: TFunction, navigate: NavigateFunction, showDeleted: boolean, organizations: IOrganizationStore, pauses: IPauseStore, queues: IQueueStore, teams: ITeamStore, abilities: IAbilities, auth: IAuth, buttons: Array<(row: IPreviewAgentprofile) => React.ReactElement> = [], filters: IWithListProps<IPreviewAgentprofile, TAgentprofileSortableListCol>['filters'], sorter: IWithListProps<IPreviewAgentprofile, TAgentprofileSortableListCol>['sorter']): ColumnsType<IPreviewAgentprofile> => {
  return [
    {
      title: t('ID'),
      dataIndex: 'id',
      key: 'id',
      sorter: (a: IPreviewAgentprofile, b: IPreviewAgentprofile) => a.id - b.id,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'id')?.order || null,
      filteredValue: filters.id || null,
      ...GetColumnSearchEqualProps(t, 'id', t('ID')),
      className: 'table-cell-id',
    },
    auth.user.__admin_organization_ids.length > 1 && {
      title: t('ORGANIZATION'),
      dataIndex: 'organization_id',
      key: 'organization_id',
      render: (organization_id: IPreviewAgentprofile['organization_id'], agentprofile: IPreviewAgentprofile) => organizations.dict?.[organization_id]?.title || t('ORGANIZATION_NOT_FOUND', { replace: { organization_id, }, }),
      sorter: (a: IPreviewAgentprofile, b: IPreviewAgentprofile) => (organizations.dict?.[a.organization_id]?.title || '').localeCompare(organizations.dict?.[b.organization_id]?.title || ''),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'organization_id')?.order || null,
      filteredValue: filters.organization_id || null,
      ...GetColumnSearchProps(t, 'organization_id', t('ORGANIZATION'), organizations.organizations),
      className: 'table-cell-organization_id',
    },
    {
      title: t('TITLE'),
      dataIndex: 'title',
      key: 'title',
      sorter: (a: IPreviewAgentprofile, b: IPreviewAgentprofile) => tableColumnStringSorter(a.title, b.title),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'title')?.order || null,
      filteredValue: filters.title || null,
      ...GetColumnSearchLikeProps(t, 'title', t('TITLE')),
      className: 'table-cell-title',
    },
    {
      title: t('__PAUSES'),
      dataIndex: '__pauses',
      key: '__pauses',
      render: (__pauses: IPreviewAgentprofile['__pauses'], agentprofile: IPreviewAgentprofile) => <div className='length-column'>{__pauses.length}</div>,
      sorter: (a: IPreviewAgentprofile, b: IPreviewAgentprofile) => a.__pauses.length - b.__pauses.length,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === '__pauses')?.order || null,
      className: 'table-cell-__pauses',
    },
    {
      title: t('__QUEUES'),
      dataIndex: '__queues',
      key: '__queues',
      render: (__queues: IPreviewAgentprofile['__queues'], agentprofile: IPreviewAgentprofile) => <div className='length-column'>{__queues.length}</div>,
      sorter: (a: IPreviewAgentprofile, b: IPreviewAgentprofile) => a.__queues.length - b.__queues.length,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === '__queues')?.order || null,
      className: 'table-cell-__queues',
    },
    {
      title: t('__TEAMS'),
      dataIndex: '__teams',
      key: '__teams',
      render: (__teams: IPreviewAgentprofile['__teams'], agentprofile: IPreviewAgentprofile) => <div className='length-column'>{__teams.length}</div>,
      sorter: (a: IPreviewAgentprofile, b: IPreviewAgentprofile) => a.__teams.length - b.__teams.length,
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === '__teams')?.order || null,
      className: 'table-cell-__teams',
    },
    {
      title: t('CREATED_AT'),
      dataIndex: 'created_at',
      key: 'created_at',
      render: (created_at: IPreviewAgentprofile['created_at'], agentprofile: IPreviewAgentprofile) => <div className='date-column'>{created_at ? timeSince(t, new Date(created_at), true) : ''}</div>,
      sorter: (a: IPreviewAgentprofile, b: IPreviewAgentprofile) => tableColumnDatetimeSorter(a.created_at, b.created_at),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'created_at')?.order || null,
      className: 'table-cell-created_at',
    },
    showDeleted && { 
      title: t('DELETED'), 
      dataIndex: 'deleted_at',
      key: 'deleted_at',
      className: 'table-cell-deleted_at',
      render: (deleted_at: IPreviewAgentprofile['deleted_at']) => deleted_at ? timeSince(t, new Date(deleted_at), true) : '',
      sorter: (a: IPreviewAgentprofile, b: IPreviewAgentprofile) => tableColumnDatetimeSorter(a.deleted_at, b.deleted_at),
      sortDirections: ['descend', 'ascend', ],
      sortOrder: sorter.find(s => s.column === 'deleted_at')?.order || null,
    },
    {
      title: '',
      dataIndex: 'operation',
      key: 'operation',
      render: (_: any, row: IPreviewAgentprofile) => (
        <div className={'table-operation-wrp'}>
          {!row.deleted_at && buttons.map(button => button(row))}
          {abilities.can(row.organization_id, CrudEnum.Insert, PermissionEnum.Agentprofile, row) && listCloneButton(t, navigate, 'agentprofile', row)}
          {abilities.can(row.organization_id, CrudEnum.Read, PermissionEnum.Audit, row) && listAuditButton(t, navigate, 'agentprofile', row)}
          {!row.deleted_at && abilities.can(row.organization_id, CrudEnum.Update, PermissionEnum.Agentprofile, row) && listEditButton(t, navigate, 'agentprofile', row)}
          {!row.deleted_at && abilities.can(row.organization_id, CrudEnum.Delete, PermissionEnum.Agentprofile, row) && listDeleteButton(t, row, deleteAgentprofile)}
        </div>
      ),
    },
  ].filter(Boolean) as ColumnsType<IPreviewAgentprofile>;

};
